import React, { useState } from 'react'
import { Form, FormGroup, Modal, ModalHeader, ModalBody, ModalFooter, Label, Button, Input, Alert, Progress } from 'reactstrap'
import { UpdatePricingTemplateMutation } from './Mutations'
import withApolloClient from '../withApolloClient'
import './AddPricingModal.css'

const initialInputFor = (price) => {
    const mileageUnit = (price?.agreementType?.branch?.defaultMileageUnit || price?.branch?.defaultMileageUnit) === 'KM' ? 'KM' : 'MI'
    return {
        isDefault: !!price?.isDefault,
        isActive: !!price?.isActive,
        visibleToCustomers: !!price?.visibleToCustomers,
        minimumPrice: price?.minimumPrice ?? '',
        maximumPrice: price?.maximumPrice ?? '',
        carYearMin: price?.carYearMin ?? '',
        carYearMax: price?.carYearMax ?? '',
        carMileageMin: (mileageUnit === 'KM' ? price?.carMileageMinInKm : price?.carMileageMin) ?? '',
        carMileageMax: (mileageUnit === 'KM' ? price?.carMileageMaxInKm : price?.carMileageMax) ?? '',
    }
}

const EditPricingModal = (props) => {
    const price = props.price || {}
    const mileageUnit = (price.agreementType?.branch?.defaultMileageUnit || price.branch?.defaultMileageUnit) === 'KM' ? 'KM' : 'MI'
    const [input, setInput] = useState(() => initialInputFor(props.price))
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState('')

    const handleChange = (e) => {
        const { name, type, checked, value } = e.target
        setInput((prev) => ({ ...prev, [name]: type === 'checkbox' ? checked : value }))
    }

    const validationError = () => {
        if (input.minimumPrice !== '' && input.maximumPrice !== '' && parseFloat(input.minimumPrice) > parseFloat(input.maximumPrice)) {
            return 'Minimum Price must be less than or equal to the Maximum Price'
        }
        if (input.carYearMin !== '' && input.carYearMax !== '' && parseInt(input.carYearMin) > parseInt(input.carYearMax)) {
            return 'Car Year Min must be less than or equal to Car Year Max'
        }
        if (input.carMileageMin !== '' && input.carMileageMax !== '' && parseFloat(input.carMileageMin) > parseFloat(input.carMileageMax)) {
            return 'Car Mileage Min must be less than or equal to Car Mileage Max'
        }
        return ''
    }

    const handleSubmit = (e) => {
        e.preventDefault()
        const validation = validationError()
        if (validation) {
            setError(validation)
            return
        }

        const mutationInput = {
            pricingTemplate: price.id,
            isDefault: input.isDefault,
            isActive: input.isActive,
            visibleToCustomers: input.visibleToCustomers,
            minimumPrice: parseFloat(input.minimumPrice),
            maximumPrice: parseFloat(input.maximumPrice),
        }

        // Only include vehicle eligibility fields if they have values
        if (input.carYearMin !== '') {
            mutationInput.carYearMin = parseInt(input.carYearMin)
        }
        if (input.carYearMax !== '') {
            mutationInput.carYearMax = parseInt(input.carYearMax)
        }
        if (input.carMileageMin !== '') {
            mutationInput.carMileageMin = { mileage: parseFloat(input.carMileageMin), mileageUnit }
        }
        if (input.carMileageMax !== '') {
            mutationInput.carMileageMax = { mileage: parseFloat(input.carMileageMax), mileageUnit }
        }

        setLoading(true)
        props.apolloClient.mutate({ mutation: UpdatePricingTemplateMutation, variables: { input: mutationInput } })
            .then((result) => {
                setLoading(false)
                if (result.data && result.data.updatePricingTemplate && result.data.updatePricingTemplate.ok) {
                    setError('')
                    props.refetchAllPricingTemplates()
                    props.handleClose()
                } else if (result.data && result.data.updatePricingTemplate && result.data.updatePricingTemplate.errors && result.data.updatePricingTemplate.errors[0]) {
                    setError(result.data.updatePricingTemplate.errors[0].messages.toString())
                } else {
                    setError('Something went wrong, please contact admin for support!')
                }
            })
            .catch((err) => {
                setLoading(false)
                setError(err.message)
            })
    }

    const branchName = price.agreementType?.branch?.name || price.branch?.name || '--'
    const agreementName = price.agreementType ? price.agreementType.name : 'All Agreement Types'
    const carModelName = price.carModel ? `${price.carModel.make} ${price.carModel.name}` : 'For All Models'

    return (
        <Modal isOpen={props.isOpen} className="pricing-modal">
            <ModalHeader
                close={(
                    <button type="button" className="pricing-modal__close" aria-label="Close" onClick={props.handleClose}>
                        ×
                    </button>
                )}
            >
                <span className="pricing-modal__title">Edit Pricing Template</span>
                <span className="pricing-modal__subtitle">Only the fields you can change today are editable.</span>
            </ModalHeader>
            <Form onSubmit={handleSubmit}>
                <ModalBody>
                    {error && <Alert color="danger">{error}</Alert>}
                    {(loading || props.loading) && <Progress animated color="info" value="100" />}

                    <div className="pricing-modal__layout">
                        <div className="pricing-modal__form">
                            <section className="pricing-modal__section">
                                <div className="pricing-modal__section-heading">
                                    <span>1</span>
                                    <div>
                                        <h2>Template Basics</h2>
                                        <p className="pricing-modal__subtitle">Name and visibility.</p>
                                    </div>
                                </div>
                                <div className="pricing-modal__grid">
                                    <FormGroup className="pricing-modal__field pricing-modal__span-12">
                                        <Label for="editPricingTitle">Pricing Template Title</Label>
                                        <Input type="text" id="editPricingTitle" disabled value={price.title || ''} />
                                    </FormGroup>
                                    <FormGroup className="pricing-modal__field pricing-modal__span-4">
                                        <Label for="editIsDefault">Default Template</Label>
                                        <Label className="pricing-modal__switch" for="editIsDefault">
                                            <span>Is Default</span>
                                            <input className="pricing-modal__toggle" id="editIsDefault" name="isDefault" type="checkbox" checked={input.isDefault} onChange={handleChange} />
                                        </Label>
                                    </FormGroup>
                                    <FormGroup className="pricing-modal__field pricing-modal__span-4">
                                        <Label for="editIsActive">Active</Label>
                                        <Label className="pricing-modal__switch" for="editIsActive">
                                            <span>Is Active</span>
                                            <input className="pricing-modal__toggle" id="editIsActive" name="isActive" type="checkbox" checked={input.isActive} onChange={handleChange} />
                                        </Label>
                                    </FormGroup>
                                    <FormGroup className="pricing-modal__field pricing-modal__span-4">
                                        <Label for="editVisibleToCustomers">Customer Visibility</Label>
                                        <Label className="pricing-modal__switch" for="editVisibleToCustomers">
                                            <span>Visible during booking</span>
                                            <input className="pricing-modal__toggle" id="editVisibleToCustomers" name="visibleToCustomers" type="checkbox" checked={input.visibleToCustomers} onChange={handleChange} />
                                        </Label>
                                    </FormGroup>
                                </div>
                            </section>

                            <section className="pricing-modal__section">
                                <div className="pricing-modal__section-heading">
                                    <span>2</span>
                                    <div>
                                        <h2>Placement</h2>
                                        <p className="pricing-modal__subtitle">Branch and agreement type are set at creation.</p>
                                    </div>
                                </div>
                                <div className="pricing-modal__grid">
                                    <FormGroup className="pricing-modal__field pricing-modal__span-6">
                                        <Label for="editBranch">Branch</Label>
                                        <Input type="text" id="editBranch" disabled value={branchName} />
                                    </FormGroup>
                                    <FormGroup className="pricing-modal__field pricing-modal__span-6">
                                        <Label for="editAgreementType">Agreement Type</Label>
                                        <Input type="text" id="editAgreementType" disabled value={agreementName} />
                                    </FormGroup>
                                </div>
                            </section>

                            <section className="pricing-modal__section">
                                <div className="pricing-modal__section-heading">
                                    <span>3</span>
                                    <div>
                                        <h2>Rate &amp; Cadence</h2>
                                        <p className="pricing-modal__subtitle">Only the price range can change after creation.</p>
                                    </div>
                                </div>
                                <div className="pricing-modal__grid">
                                    <FormGroup className="pricing-modal__field pricing-modal__span-4">
                                        <Label for="editDefaultPrice">Default Price</Label>
                                        <Input type="number" id="editDefaultPrice" disabled value={price.price ?? ''} />
                                    </FormGroup>
                                    <FormGroup className="pricing-modal__field pricing-modal__span-4">
                                        <Label for="editMinimumPrice">Minimum Price</Label>
                                        <Input type="number" step="any" min={0} name="minimumPrice" id="editMinimumPrice" value={input.minimumPrice} onChange={handleChange} />
                                    </FormGroup>
                                    <FormGroup className="pricing-modal__field pricing-modal__span-4">
                                        <Label for="editMaximumPrice">Maximum Price</Label>
                                        <Input type="number" step="any" min={0} name="maximumPrice" id="editMaximumPrice" value={input.maximumPrice} onChange={handleChange} />
                                    </FormGroup>
                                    <FormGroup className="pricing-modal__field pricing-modal__span-6">
                                        <Label for="editInterval">Interval</Label>
                                        <Input type="text" id="editInterval" disabled value={price.interval ?? ''} />
                                    </FormGroup>
                                    <FormGroup className="pricing-modal__field pricing-modal__span-6">
                                        <Label for="editIntervalUnit">Interval Unit</Label>
                                        <Input type="text" id="editIntervalUnit" disabled value={price.intervalUnit || ''} />
                                    </FormGroup>
                                </div>
                            </section>

                            <section className="pricing-modal__section">
                                <div className="pricing-modal__section-heading">
                                    <span>4</span>
                                    <div>
                                        <h2>Vehicle Eligibility</h2>
                                        <p className="pricing-modal__subtitle">Car model is fixed; year and mileage range can change.</p>
                                    </div>
                                </div>
                                <div className="pricing-modal__grid">
                                    <FormGroup className="pricing-modal__field pricing-modal__span-12">
                                        <Label for="editCarModel">Car Model</Label>
                                        <Input type="text" id="editCarModel" disabled value={carModelName} />
                                    </FormGroup>
                                    <FormGroup className="pricing-modal__field pricing-modal__span-6">
                                        <Label for="editCarYearMin">Car Year Min</Label>
                                        <Input type="number" name="carYearMin" id="editCarYearMin" min={2000} max={3000} value={input.carYearMin} onChange={handleChange} />
                                    </FormGroup>
                                    <FormGroup className="pricing-modal__field pricing-modal__span-6">
                                        <Label for="editCarYearMax">Car Year Max</Label>
                                        <Input type="number" name="carYearMax" id="editCarYearMax" min={input.carYearMin || 2000} max={3000} value={input.carYearMax} onChange={handleChange} />
                                    </FormGroup>
                                    <FormGroup className="pricing-modal__field pricing-modal__span-6">
                                        <Label for="editCarMileageMin">Car Mileage Min ({mileageUnit})</Label>
                                        <Input type="number" name="carMileageMin" id="editCarMileageMin" min={0} value={input.carMileageMin} onChange={handleChange} />
                                    </FormGroup>
                                    <FormGroup className="pricing-modal__field pricing-modal__span-6">
                                        <Label for="editCarMileageMax">Car Mileage Max ({mileageUnit})</Label>
                                        <Input type="number" name="carMileageMax" id="editCarMileageMax" min={input.carMileageMin || 0} value={input.carMileageMax} onChange={handleChange} />
                                    </FormGroup>
                                </div>
                            </section>
                        </div>

                        <aside className="pricing-modal__summary" aria-label="Pricing summary">
                            <h2>Pricing Summary</h2>
                            <p className="pricing-modal__subtitle">Reflects your unsaved changes.</p>
                            <div className="pricing-modal__summary-card">
                                <div className="pricing-modal__summary-value">
                                    <small>{price.title || 'Default Price'}</small>
                                    <strong>${price.price ?? '—'}<span>per {price.interval} {price.intervalUnit}</span></strong>
                                </div>
                                <dl>
                                    <div>
                                        <dt>Range</dt>
                                        <dd>${input.minimumPrice || '—'} – ${input.maximumPrice || '—'}</dd>
                                    </div>
                                    <div>
                                        <dt>Branch</dt>
                                        <dd>{branchName}</dd>
                                    </div>
                                    <div>
                                        <dt>Agreement</dt>
                                        <dd>{agreementName}</dd>
                                    </div>
                                    <div>
                                        <dt>Vehicle</dt>
                                        <dd>{carModelName}</dd>
                                    </div>
                                </dl>
                            </div>
                        </aside>
                    </div>
                </ModalBody>
                <ModalFooter>
                    <div className="pricing-modal__actions">
                        <Button color="secondary" type="button" onClick={props.handleClose}>Cancel</Button>
                        <Button color="primary" type="submit" disabled={loading}>Save Changes</Button>
                    </div>
                </ModalFooter>
            </Form>
        </Modal>
    )
}

export default withApolloClient(EditPricingModal)
